import React, { useState, useEffect } from 'react';
import { Plus, Calendar, DollarSign, TrendingDown, Edit, Trash2, Filter } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ExpensesManagement = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    is_recurring: false,
    recurrence_type: ''
  });

  const [stats, setStats] = useState({
    monthlyExpenses: 0,
    oneTimeExpenses: 0,
    totalExpenses: 0
  });

  const expenseCategories = [
    'Grupo',
    'Ferramenta',
    'Software',
    'Assinatura',
    'Curso',
    'Outros'
  ];

  const recurrenceTypes = [
    { value: 'monthly', label: 'Mensal' },
    { value: 'weekly', label: 'Semanal' }
  ];

  useEffect(() => {
    if (user) {
      loadExpenses();
    }
  }, [user, selectedPeriod, customStartDate, customEndDate]);

  useEffect(() => {
    calculateStats();
  }, [expenses]);

  const getPeriodDates = () => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedPeriod) {
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'all_time':
        return null;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          return null;
        }
        break;
      default:
        return null;
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const calculateStats = () => {
    const monthlyExpenses = expenses
      .filter(expense => expense.is_recurring)
      .reduce((sum, expense) => sum + (expense.amount || 0), 0);

    const oneTimeExpenses = expenses
      .filter(expense => !expense.is_recurring)
      .reduce((sum, expense) => sum + (expense.amount || 0), 0);

    const totalExpenses = monthlyExpenses + oneTimeExpenses;

    setStats({
      monthlyExpenses,
      oneTimeExpenses,
      totalExpenses
    });
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const periodDates = getPeriodDates();
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/expenses?user_id=${user.id}`;
      
      if (periodDates) {
        url += `&start_date=${periodDates.startDate}&end_date=${periodDates.endDate}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setExpenses(data);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        category: expense.category,
        description: expense.description,
        amount: expense.amount.toString(),
        date: new Date(expense.date).toISOString().split('T')[0],
        is_recurring: expense.is_recurring,
        recurrence_type: expense.recurrence_type || ''
      });
    } else {
      setEditingExpense(null);
      setFormData({
        category: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        is_recurring: false,
        recurrence_type: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    setFormData({
      category: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      is_recurring: false,
      recurrence_type: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      const payload = {
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount),
        date: formData.date,
        is_recurring: formData.is_recurring,
        recurrence_type: formData.is_recurring ? formData.recurrence_type : null
      };

      let response;
      if (editingExpense) {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expenses/${editingExpense.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expenses?user_id=${user.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingExpense ? 'Despesa atualizada com sucesso!' : 'Despesa criada com sucesso!');
        closeModal();
        loadExpenses();
      } else {
        throw new Error('Falha ao salvar');
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!confirm('Tem certeza que deseja excluir esta despesa?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expenses/${expenseId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Despesa excluída com sucesso!');
        loadExpenses();
      } else {
        throw new Error('Falha ao excluir');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Erro ao excluir. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      {/* Header Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            💸 Outras Despesas
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
            Gerencie gastos com grupos, ferramentas e outras despesas mensais
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-gradient-to-r from-red-600 to-pink-600 text-white px-4 py-3 rounded-lg hover:from-red-700 hover:to-pink-700 transition-colors font-medium shadow-lg"
        >
          <Plus className="w-5 h-5" />
          <span>Nova Despesa</span>
        </button>
      </div>

      {/* Period Filter Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Filter className="w-5 h-5 mr-2 text-red-600" />
            Filtrar por Período
          </h3>
          {loading && (
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
              Carregando...
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="current_month">📅 Mês Atual</option>
            <option value="last_month">📆 Mês Anterior</option>
            <option value="last_3_months">📊 Últimos 3 Meses</option>
            <option value="all_time">🌐 Todo o Período</option>
            <option value="custom">🎯 Período Personalizado</option>
          </select>
          
          {selectedPeriod === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Data inicial"
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Data final"
              />
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">Despesas Mensais</h3>
          <p className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">{formatCurrency(stats.monthlyExpenses)}</p>
          <p className="text-xs text-gray-500 mt-1">
            {expenses.filter(e => e.is_recurring).length} despesas ativas
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">Despesas Únicas</h3>
          <p className="text-xl sm:text-2xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(stats.oneTimeExpenses)}</p>
          <p className="text-xs text-gray-500 mt-1">
            {expenses.filter(e => !e.is_recurring).length} despesas registradas
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-pink-700 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">Total de Despesas</h3>
          <p className="text-xl sm:text-2xl font-bold text-red-700 dark:text-red-400">{formatCurrency(stats.totalExpenses)}</p>
          <p className="text-xs text-gray-500 mt-1">Impacto no resultado final</p>
        </div>
      </div>

      {/* Expenses List Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Despesas Cadastradas ({expenses.length})
          </h3>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Carregando...</p>
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <DollarSign className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
                Nenhuma despesa cadastrada
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Comece adicionando suas despesas mensais com grupos e ferramentas.
              </p>
              <button
                onClick={() => openModal()}
                className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-red-700 hover:to-pink-700 transition-colors"
              >
                Adicionar Primeira Despesa
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {expenses.map(expense => (
                <div key={expense.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Icon and Info */}
                    <div className="flex items-center space-x-3 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${expense.is_recurring ? 'bg-gradient-to-r from-red-500 to-pink-600' : 'bg-gradient-to-r from-orange-500 to-red-600'}`}>
                        {expense.is_recurring ? <Calendar className="w-5 h-5 text-white" /> : <DollarSign className="w-5 h-5 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-white truncate">{expense.description}</h4>
                        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500 mt-1">
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded font-medium">
                            {expense.category}
                          </span>
                          {expense.is_recurring && (
                            <span className="px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded font-medium">
                              {expense.recurrence_type === 'monthly' ? 'Mensal' : 'Semanal'}
                            </span>
                          )}
                          <span className="truncate">{formatDate(expense.date)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Amount and Actions */}
                    <div className="flex items-center justify-between sm:justify-end space-x-3 ml-13 sm:ml-0">
                      <div className="text-left sm:text-right">
                        <p className="text-lg font-bold text-red-600 dark:text-red-400">
                          -{formatCurrency(expense.amount)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {expense.is_recurring ? 'Recorrente' : 'Única'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => openModal(expense)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Mobile Optimized */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingExpense ? 'Editar Despesa' : 'Nova Despesa'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categoria *
                </label>
                <select
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Selecione uma categoria</option>
                  {expenseCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="description"
                  required
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Ex: Assinatura Grupo VIP"
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor (R$) *
                </label>
                <input
                  type="number"
                  name="amount"
                  required
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Ex: 50.00"
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data *
                </label>
                <input
                  type="date"
                  name="date"
                  required
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="is_recurring"
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Despesa recorrente
                </label>
              </div>

              {formData.is_recurring && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Recorrência *
                  </label>
                  <select
                    name="recurrence_type"
                    required={formData.is_recurring}
                    value={formData.recurrence_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Selecione a recorrência</option>
                    {recurrenceTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full sm:w-auto px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg hover:from-red-700 hover:to-pink-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : editingExpense ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesManagement;